export type DymoSlideCompletionMap = Record<string, Record<string, boolean>>;

interface DymoSpecimen {
  sourceId?: string | null;
  material?: string | null;
  blockCount?: number | string | null;
}

interface ResolvedDymoStain {
  id: string;
  selectionId?: string;
  markAsCompleted?: boolean;
}

interface DymoEligibilityInput {
  specimens: DymoSpecimen[];
  resolvedStains: Record<number, ResolvedDymoStain[]>;
  explicitCompletions: DymoSlideCompletionMap;
}

const SLIDE_BEARING_MATERIALS = new Set(["slides_only", "blocks_and_slides"]);

const hasEligibleSource = (specimen: DymoSpecimen) =>
  !Object.prototype.hasOwnProperty.call(specimen, "sourceId") ||
  Boolean(specimen.sourceId);

const hasOwnCompletion = (
  completions: Record<string, boolean>,
  stainId: string
) => Object.prototype.hasOwnProperty.call(completions, stainId);

const deriveSpecimenCompletions = (
  stains: ResolvedDymoStain[],
  explicit: Record<string, boolean>
) => {
  const completions: Record<string, boolean> = {};
  stains.forEach((stain) => {
    const selectionId = stain.selectionId ?? stain.id;
    completions[selectionId] = hasOwnCompletion(explicit, selectionId)
      ? explicit[selectionId]
      : hasOwnCompletion(explicit, stain.id)
      ? explicit[stain.id]
      : Boolean(stain.markAsCompleted);
  });

  return completions;
};

export const deriveEffectiveDymoSlideCompletions = ({
  specimens,
  resolvedStains,
  explicitCompletions,
}: DymoEligibilityInput): DymoSlideCompletionMap => {
  const effective: DymoSlideCompletionMap = {};

  specimens.forEach((specimen, specimenIndex) => {
    if (!hasEligibleSource(specimen)) return;
    if (!SLIDE_BEARING_MATERIALS.has(specimen.material || "")) return;

    const completions = deriveSpecimenCompletions(
      resolvedStains[specimenIndex] || [],
      explicitCompletions[specimenIndex] || {}
    );
    if (Object.keys(completions).length > 0)
      effective[specimenIndex] = completions;
  });

  return effective;
};

const getBlockCount = (specimens: DymoSpecimen[], specimenIndex: string) =>
  Number(specimens[Number(specimenIndex)]?.blockCount) || 1;

export const countCompletedDymoSlides = (completions: DymoSlideCompletionMap) =>
  Object.values(completions).reduce(
    (total, stains) => total + Object.values(stains).filter(Boolean).length,
    0
  );

export const countResolvedDymoSlides = (completions: DymoSlideCompletionMap) =>
  Object.values(completions).reduce(
    (total, stains) => total + Object.keys(stains).length,
    0
  );

export const countPrintableDymoLabels = (
  completions: DymoSlideCompletionMap,
  specimens: DymoSpecimen[]
) =>
  Object.entries(completions).reduce(
    (total, [specimenIndex, stains]) =>
      total +
      Object.values(stains).filter(Boolean).length *
        getBlockCount(specimens, specimenIndex),
    0
  );
